import { createFileRoute } from '@tanstack/react-router';
import { useEffect } from 'react';
import { Seo } from '@/components/seo';
import { useTheme } from '../hooks/use-theme';
import { HeroHeader } from '../components/header';
import FooterSection from '../components/footer';
import { TextEffect } from '../components/motion-primitives/text-effect';
import { Badge } from '@/components/ui/badge';

export const Route = createFileRoute('/about')({
  component: AboutPage,
});

function AboutPage() {
  const { theme, setTheme } = useTheme();

  useEffect(() => {
    const originalTheme = theme;
    setTheme('light');
    return () => {
      setTheme(originalTheme);
    };
    // Intentionally run once on mount only: this captures the theme at mount time and
    // restores it on unmount. Adding `theme` would re-run after setTheme('light') fires
    // and overwrite `originalTheme` with 'light'; `setTheme` is recreated every
    // ThemeProvider render so adding it would also cause spurious re-runs.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <Seo
        title="About Boundr — Protecting What's Yours, From Wherever You Are"
        description="Boundr gives NRI landowners visibility and peace of mind with regular, verified, on-ground inspections of their property in India — delivered digitally."
        path="/about"
      />
      <HeroHeader />

      <main>
        {/* Hero */}
        <section className="pt-24 md:pt-36 pb-16">
          <div className="mx-auto max-w-5xl px-6 text-center">
            <Badge variant="outline" className="mb-6">
              Our Story
            </Badge>
            {/* Matches the /land-monitoring hero register: Fraunces at `type-wonk`
                (SOFT 40 · WONK 1), font-normal — Fraunces sets heavier than the
                sans did, so the scale steps down rather than up. */}
            <h1 className="type-wonk mb-6 text-balance font-display text-4xl font-normal leading-[1.12] tracking-tight text-foreground sm:text-5xl md:text-[3.4rem]">
              About Boundr
            </h1>
            <TextEffect
              per="line"
              preset="fade-in-blur"
              speedReveal={0.3}
              delay={0.3}
              as="p"
              className="mx-auto max-w-2xl text-balance text-lg text-foreground/65"
            >
              Protecting what's yours, from wherever you are.
            </TextEffect>
          </div>
        </section>

        {/* Mission */}
        <section className="py-16 md:py-24">
          <div className="mx-auto max-w-5xl px-6">
            <div className="mx-auto max-w-3xl text-center">
              <h2 className="mb-6 font-display text-3xl font-medium leading-tight tracking-tight text-foreground md:text-4xl">
                Our Mission
              </h2>
              <p className="text-lg leading-relaxed text-foreground/65">
                Millions of families own land in India but live thousands of miles away. Boundr
                exists to give them the visibility and peace of mind they deserve — with regular,
                verified, on-ground inspections delivered digitally.
              </p>
              <p className="mt-4 text-lg leading-relaxed text-foreground/65">
                We believe every landowner has the right to know what's happening on their property,
                without expensive travel or unreliable intermediaries.
              </p>
            </div>
          </div>
        </section>
      </main>

      <FooterSection />
    </>
  );
}
